import { dateToIso } from '@/lib/utils/date';
import { CalendarDayCell } from './CalendarDayCell';
import type { CalendarEntry } from '../api/calendar-api';
import { buildMonthGrid, groupByDate } from '@/lib/calendar/monthGrid';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * Six rows of seven, always.
 *
 * `gap-px` over a tinted parent draws the hairlines: 42 bordered cells would
 * double up at every shared edge, and the seams would be twice the weight of
 * the outer ones.
 */
export function MonthGrid({
  month,
  today,
  entries,
  selectedDate,
  onSelectDate,
  completeThrough,
}: {
  completeThrough: string | null;
  entries: CalendarEntry[];
  month: string;
  today: string;
  selectedDate: string | null;
  onSelectDate: (date: string) => void;
}) {
  const days = buildMonthGrid(month);
  const byDate = groupByDate(entries);

  return (
    <div className="overflow-hidden rounded-[18px] border border-border">
      <div className="grid grid-cols-7 border-b border-border bg-card">
        {WEEKDAYS.map((day) => (
          <div
            key={day}
            className="px-2 py-2.5 text-center text-[11px] tracking-[0.14em] text-muted-foreground uppercase"
          >
            {day}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 grid-rows-6 gap-px bg-border">
        {days.map((day) => {
          const iso = dateToIso(day);

          return (
            <CalendarDayCell
              key={iso}
              date={iso}
              isToday={iso === today}
              entries={byDate.get(iso) ?? []}
              selected={selectedDate === iso}
              inMonth={iso.slice(0, 7) === month}
              unshown={completeThrough !== null && iso > completeThrough}
              onSelect={onSelectDate}
            />
          );
        })}
      </div>
    </div>
  );
}
